/**
 * Public ArticleDetail — single article view. Fetches from
 * /api/cms/public/articles/:slug at runtime, or accepts a build-time
 * preload via PreloadContext (PR2 Phase 7 — see scripts/prerender.tsx).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link, Navigate } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { Container } from '@/components/ui'
import { H1 } from '@/components/ui/typography'
import SEO from '@/utils/seo'
import { buildArticle, buildBreadcrumbList } from '@/utils/structuredData'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import CmsImage from '@/components/media/CmsImage'
import { mediaAltForLocale } from '@/cms/mediaAlt'
import { previewTokenParam } from '@/utils/previewToken'
import { isTrustedEmbedUrl } from '@/admin/lib/video-urls'
import { renderInlineMarkdownWithShortcodes } from '@/utils/inlineMarkdown'
import ShortcodeBlock from '@/components/embed/ShortcodeBlock'
import { usePreload } from '@/entry-server'

/**
 * The article body's inline images. The shared renderer defaults to the
 * lecture body's spacing, so the article keeps its own.
 */
const ARTICLE_IMG_CLASS = 'rounded-yada-md border border-border shadow-yada my-4'

/**
 * The `sizes` for the article COVER.
 *
 * The cover sits in `<Container className="max-w-3xl">` — a 768px column —
 * and is `w-full`, so the column width IS the layout width. On a phone the
 * container's side padding takes a slice off the viewport, so '100vw'
 * overstates it there, which is the safe direction (a too-small `sizes` makes
 * the browser upscale; a too-large one only costs a few kilobytes).
 */
const ARTICLE_COVER_SIZES = '(min-width: 768px) 768px, 100vw'

interface ArticlePayload {
  slug: string
  title: string
  excerpt: string
  body_md: string
  cover_media_id: number | null
  cover_media_uuid: string | null
  published_at: number | null
  seo: Record<string, unknown>
}

interface PublicArticleResp {
  locale: string
  article: ArticlePayload
}

export default function ArticleDetail() {
  const { slug = '' } = useParams<{ slug: string }>()
  const { i18n, t } = useTranslation()
  const locale = i18n.language as 'zh-Hant' | 'zh-Hans' | 'en'
  // PR2 Phase 7: prerender passes the full CMS row via PreloadContext so the
  // initial render already has the body (CSR's useEffect never fires in SSR).
  // At runtime this hook returns null and the page falls through to fetch.
  const preloaded = usePreload()
  const [data, setData] = useState<ArticlePayload | null>(
    preloaded?.kind === 'article' ? (preloaded.data as PublicArticleResp).article : null,
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Preload path: data already populated, no fetch needed at build time.
    if (preloaded) return
    setData(null)
    setError(null)
    fetch(`/api/cms/public/articles/${encodeURIComponent(slug)}?locale=${locale}${previewTokenParam()}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`http_${r.status}`)
        const j = (await r.json()) as PublicArticleResp
        setData(j.article)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'fetch_failed'))
  }, [slug, locale, preloaded])

  if (error === 'http_404') return <Navigate to="/articles" replace />
  if (error) {
    return (
      <section className="py-section-y px-section-x">
        <Container>
          <p className="text-danger">無法載入文章：{error}</p>
        </Container>
      </section>
    )
  }
  if (!data) {
    return (
      <section className="py-section-y px-section-x">
        <Container>
          <p className="text-ink-muted">載入中…</p>
        </Container>
      </section>
    )
  }

  const cover = data.cover_media_uuid ? cmsMediaIndex[data.cover_media_uuid] : undefined
  // The ORIGINAL stays the social-preview image: og:image consumers (Facebook,
  // LinkedIn) still expect a JPEG/PNG, and the original is the biggest file
  // the CMS has. The RENDERED cover below is served from the responsive
  // ladder instead.
  const coverUrl = cover?.urls.original ?? cover?.urls.medium ?? cover?.urls.thumb
  // The library's alt for the READER's script (an empty column counts as
  // "not filled in" and is skipped), falling back to the article's title —
  // which is also what a screen reader needs to hear for a cover image.
  const coverAlt = mediaAltForLocale(cover, locale) || data.title
  const seoMeta = data.seo as { metaTitle?: string; metaDescription?: string }

  function inlineRender(s: string): React.ReactNode {
    // The shared renderer — the same one the lecture body uses. It was
    // duplicated here until 2026-10-29, which meant a bare URL was plain
    // text on a lecture but a link on an article. Shortcodes `{{key}}` are
    // expanded into embed components alongside the inline Markdown subset.
    return renderInlineMarkdownWithShortcodes(s, { imageClassName: ARTICLE_IMG_CLASS })
  }

  function renderVideoFence(rawHtml: string): React.ReactNode {
    // 2026-10-12: added iframe support for YouTube/Vimeo/Bilibili (P2 of the
    // AI-assist import plan). DOMPurify strips event handlers and script tags
    // before we even look at the parsed tree, then the trusted-embed allowlist
    // (isTrustedEmbedUrl) ensures the iframe src is one of the known video
    // providers — anything outside that list is silently dropped, so a body_md
    // hand-edited by a careless operator can't exfiltrate via a malicious
    // embed URL. The existing <video> path is preserved as-is; both render
    // into the same wrapping div in source order (DOMParser order).
    if (typeof window === 'undefined') return null
    const clean = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['video', 'iframe', 'source'],
      ALLOWED_ATTR: [
        'src',
        'type',
        'width',
        'height',
        'controls',
        'allow',
        'allowfullscreen',
        'frameborder',
        'style',
      ],
    })
    const doc = new DOMParser().parseFromString(clean, 'text/html')
    const videos = Array.from(doc.querySelectorAll('video'))
    const iframes = Array.from(doc.querySelectorAll('iframe'))
    if (videos.length === 0 && iframes.length === 0) return null
    return (
      <div className="my-4 space-y-3">
        {videos.map((v, i) => {
          const sources = Array.from(v.querySelectorAll('source'))
          const src = v.getAttribute('src') || sources[0]?.getAttribute('src') || ''
          return (
            <video
              key={`v${i}`}
              src={src || undefined}
              controls={v.hasAttribute('controls')}
              className="block w-full rounded-yada-md border border-border shadow-yada"
            />
          )
        })}
        {iframes.map((f, i) => {
          const src = f.getAttribute('src') ?? ''
          if (!isTrustedEmbedUrl(src)) return null
          // Default `allow` covers YouTube/Vimeo/Bilibili; the writer can
          // override per-embed in the source HTML if a provider needs more.
          const allow =
            f.getAttribute('allow') ??
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          return (
            <iframe
              key={`i${i}`}
              src={src}
              allow={allow}
              allowFullScreen={f.hasAttribute('allowfullscreen')}
              loading="lazy"
              className="block w-full aspect-video rounded-yada-md border border-border shadow-yada"
            />
          )
        })}
      </div>
    )
  }

  function renderMarkdown(md: string): React.ReactNode {
    // 2026-10-12: dompurify is now wired in `renderVideoFence` (above) for
    // the ```` ```html ```` fence path. The hand-rolled inline regex below
    // only ever produces <strong>/<em>/<code>/<a>/<img> from the admin's
    // own Markdown input, so the surrounding render path is XSS-safe.

    // Template-bound articles prefix body_md with a JSON HTML-comment carrying
    // the structured fields (`<!-- template-fields:{...} -->`). It's a wire-
    // format artifact and must never reach the public page as visible text,
    // so strip it before any markdown processing. Mirrors the helper in
    // src/admin/lib/template-fields.ts — the comment format is owned there.
    const cleaned = md.replace(/^<!-- template-fields:[\s\S]*?-->\s*/, '')
    // Pull out fenced-html blocks first; emit via a controlled renderer.
    const fenceRe = /```html\n([\s\S]*?)\n```/g
    const fences: string[] = []
    const stripped = cleaned.replace(fenceRe, (_m, body: string) => {
      fences.push(body.trim())
      return `\n\n__FENCE_${fences.length - 1}__\n\n`
    })
    const blocks = stripped.split(/\n{2,}/)
    return blocks.map((raw, idx) => {
      const text = raw.trim()
      if (!text) return null
      const fenceHit = text.match(/^__FENCE_(\d+)__$/)
      if (fenceHit) {
        return <div key={`f${idx}`}>{renderVideoFence(fences[Number(fenceHit[1])] ?? '')}</div>
      }
      if (text === '---') return <hr key={idx} className="my-6 border-border" />
      // A heading may only hold phrasing content, so a `{{shortcode}}` embed
      // inside one has to be lifted OUT and become a sibling — ShortcodeBlock
      // does that split. (See src/components/embed/ShortcodeBlock.tsx.)
      if (text.startsWith('## ')) return <ShortcodeBlock key={idx} as="h2" className="text-2xl font-bold mt-8 mb-3 text-ink" text={text.slice(3)} options={{ imageClassName: ARTICLE_IMG_CLASS }} />
      if (text.startsWith('### ')) return <ShortcodeBlock key={idx} as="h3" className="text-xl font-bold mt-6 mb-2 text-ink" text={text.slice(4)} options={{ imageClassName: ARTICLE_IMG_CLASS }} />
      if (text.startsWith('> ')) {
        return (
          <blockquote key={idx} className="border-l-4 border-primary pl-4 my-4 italic text-ink-soft">
            {inlineRender(text.slice(2))}
          </blockquote>
        )
      }
      if (/^[-*]\s+/.test(text) || /^\d+\.\s+/.test(text)) {
        const isOrdered = /^\d+\.\s+/.test(text)
        const Tag = isOrdered ? 'ol' : 'ul'
        const items = text.split(/\n/).filter((l) => /^([-*]|\d+\.)\s+/.test(l))
        return (
          <Tag key={idx} className="list-disc pl-6 my-4 space-y-1">
            {items.map((line, i) => {
              const stripped = line.replace(/^([-*]|\d+\.)\s+/, '')
              return <li key={i}>{inlineRender(stripped)}</li>
            })}
          </Tag>
        )
      }
      // Same split for a paragraph: the embed is block-level content and may
      // not sit inside a <p>.
      return <ShortcodeBlock key={idx} as="p" className="mb-4 leading-relaxed" text={text} options={{ imageClassName: ARTICLE_IMG_CLASS }} />
    })
  }
  return (
    <>
      <SEO
        title={seoMeta.metaTitle ?? data.title}
        description={seoMeta.metaDescription ?? data.excerpt}
        keywords={t('seo.articlesKeywords')}
        type="article"
        ogImage={coverUrl}
        articlePublishedTime={
          data.published_at ? new Date(data.published_at).toISOString() : undefined
        }
        articleAuthor={t('common.brand')}
        jsonLd={[
          buildArticle({
            path: `/articles/${data.slug}`,
            headline: seoMeta.metaTitle ?? data.title,
            alternativeHeadline: data.excerpt,
            datePublished: data.published_at
              ? new Date(data.published_at).toISOString()
              : new Date().toISOString(),
            author: t('common.brand'),
            image: coverUrl ?? '/og-image-1200x630.png',
            section: '文章 Articles',
            keywords: [
              'YADA',
              '品格教育',
              'AI 升學',
              '家校合作',
              '生涯規劃',
              '教育傳媒',
            ],
            type: 'Article',
          }),
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.articles'), path: '/articles' },
            { name: data.title, path: `/articles/${data.slug}` },
          ]),
        ]}
      />
      <article aria-labelledby="article-detail-title">
        <section className="py-section-y px-section-x bg-surface">
          <Container className="max-w-3xl">
            <p className="text-sm text-ink-muted mb-4">
              <Link to="/articles" className="hover:underline">← 所有文章</Link>
            </p>
            <header className="mb-8">
              <H1 id="article-detail-title" className="mb-3">
                {data.title}
              </H1>
              {data.published_at && (
                <time className="text-xs text-ink-muted" dateTime={new Date(data.published_at).toISOString()}>
                  {new Date(data.published_at).toLocaleDateString('zh-HK', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
              )}
            </header>
            {data.excerpt && (
              <p className="text-lg text-ink-soft italic border-l-4 border-primary pl-4 mb-8">
                {data.excerpt}
              </p>
            )}
            {/* Cover moved inline as the first body image. Previously this
                was a full-bleed 16:9 banner above the title, which dominated
                the viewport and ate the first screen. Inlining it keeps the
                reading column the same width as the body and turns the cover
                into a proper lead image rather than a hero block. */}
            {coverUrl && (
              // Phase 1 mobile (2026-10-13): the previous `-mx-2` bleed
              // was a workaround for `px-section-x` not reaching the
              // viewport edge on phones, but the reading column already
              // sits inside a container with that exact padding — the
              // negative margin made the cover wider than the body text
              // underneath and broke the reading rhythm. Drop the bleed.
              // Also: switch from `h-auto max-h-[60vh]` (which let the
              // cover grow to half the viewport) to a 16:9 frame on
              // mobile / 3:2 on desktop, with rounded corners scaled
              // down to match the rest of the site's compact density.
              <figure className="mb-6 md:mb-8">
                <CmsImage
                  uuid={data.cover_media_uuid}
                  alt={coverAlt}
                  sizes={ARTICLE_COVER_SIZES}
                  loading="eager"
                  className="block w-full aspect-[16/9] md:aspect-[3/2] object-cover rounded-yada-md md:rounded-yada-lg border border-border shadow-yada"
                />
              </figure>
            )}
            <div className="prose prose-base max-w-none text-ink">
              {renderMarkdown(data.body_md)}
            </div>
          </Container>
        </section>
      </article>
    </>
  )
}
